/** Max buffered events before the SDK is available. Prevents unbounded memory growth. */
const MAX_QUEUE_SIZE = 100;
/** Events tracked before the SDK is ready. Flushed when window.xo becomes available. */
const eventQueue = [];
const state = {
    initialized: false,
    enabled: false,
    debug: false,
    sdkInitialized: false,
    trackerKey: '',
    region: undefined,
};
const log = (...args) => {
    if (state.debug) {
        console.debug('[Fredhopper]', ...args);
    }
};
/**
 * Resolves window.xo and, on first successful resolution, calls xo.activity.init().
 * Handles the race where React initializes before AEM's XO stub script has run.
 */
const ensureInitialized = () => {
    var _a;
    if (typeof window === 'undefined') {
        return undefined;
    }
    const sdk = ((_a = window.xo) === null || _a === void 0 ? void 0 : _a.activity) ? window.xo : undefined;
    if (sdk && !state.sdkInitialized) {
        sdk.activity.init({ trackerKey: state.trackerKey, region: state.region });
        state.sdkInitialized = true;
        log('Initialized');
    }
    return sdk;
};
const dispatchToSdk = (sdk, { action, target, metadata, sourceId }) => {
    log(action, target, metadata, sourceId);
    sdk.activity.send(Object.assign({ action,
        target, metadata: metadata !== null && metadata !== void 0 ? metadata : {} }, (sourceId && { sourceId })));
};
const flushQueue = (sdk) => {
    if (eventQueue.length === 0) {
        return;
    }
    log('Flushing', eventQueue.length, 'queued events');
    while (eventQueue.length > 0) {
        dispatchToSdk(sdk, eventQueue.shift());
    }
};
const processEvent = (event) => {
    const sdk = ensureInitialized();
    if (!sdk) {
        if (eventQueue.length < MAX_QUEUE_SIZE) {
            eventQueue.push(event);
        }
        return;
    }
    flushQueue(sdk);
    dispatchToSdk(sdk, event);
};
export const initFredhopper = (config, debug = false) => {
    if (state.initialized) {
        return;
    }
    state.initialized = true;
    if (!config.isEnabled) {
        return;
    }
    state.enabled = true;
    state.debug = debug;
    state.trackerKey = config.trackerKey;
    state.region = config.region;
    const sdk = ensureInitialized();
    if (sdk) {
        flushQueue(sdk);
    }
};
/**
 * Attaches the logged-in customer's external UUID as an additional identity
 * (eCommerceId, per the SDK's user-object docs) on top of the session id the
 * SDK already manages. The SDK does not persist additional identities across
 * page loads, so this must be called again on every page load - the caller
 * (useFredhopperTracking) does this whenever the customer is logged in.
 */
export const identifyFredhopper = (externalUuid) => {
    if (!state.enabled) {
        return;
    }
    const sdk = ensureInitialized();
    if (sdk) {
        sdk.activity.addUserIdentity('eCommerceId', externalUuid);
        log('Identified', externalUuid);
    }
};
export const trackFredhopper = (action, target, metadata, sourceId) => {
    if (!state.enabled) {
        if (eventQueue.length < MAX_QUEUE_SIZE) {
            eventQueue.push({ action, target, metadata, sourceId });
        }
        return;
    }
    // Tracking must never break the caller or block other subscribers on the
    // shared emitter - mitt aborts remaining listeners if one throws (see
    // node_modules/mitt: emit() uses Array.map, which doesn't catch).
    try {
        processEvent({ action, target, metadata, sourceId });
    }
    catch (e) {
        console.error('[Fredhopper] tracking error', e);
    }
};
/** Reset all state. Only for testing — do not use in production code. */
export const resetFredhopper = () => {
    state.initialized = false;
    state.enabled = false;
    state.debug = false;
    state.sdkInitialized = false;
    state.trackerKey = '';
    state.region = undefined;
    eventQueue.length = 0;
};
